import * as React from "react";
import {
    CheckmarkCircleFilled,
    DismissCircleFilled,
    WarningFilled,
    QuestionCircleFilled,
    SubtractCircleFilled,
    ChevronDownRegular,
    ChevronRightRegular,
    SparkleFilled,
} from "@fluentui/react-icons";
import { useHealReportViewStyles } from "../styles/healReportView.styles";

/**
 * Shared report primitives used by BOTH the scout and heal report views, so
 * finding cards, info rows, section headers, and summary stats look and behave
 * identically across the two reports.
 */

export type InfoRowStatus = "fixed" | "appbug" | "flaky" | "unclassified" | "removed";

const INFO_ROW_VARIANT_CLASS: Record<InfoRowStatus, keyof ReturnType<typeof useHealReportViewStyles>> = {
    fixed: "infoRowFixed",
    appbug: "infoRowAppbug",
    flaky: "infoRowFlaky",
    unclassified: "infoRowUnclassified",
    removed: "infoRowUnclassified",
};

const INFO_ROW_ICON_COLOR: Record<InfoRowStatus, string> = {
    fixed: "#3fb950",
    appbug: "#f85149",
    flaky: "#d29922",
    unclassified: "#8b949e",
    removed: "#8b949e",
};

/** Accessible name per status, so StatusIcon announces its meaning to screen readers. */
const STATUS_LABEL: Record<InfoRowStatus, string> = {
    fixed: "Fixed",
    appbug: "App defect",
    flaky: "Flaky",
    unclassified: "Unclassified",
    removed: "Removed",
};

/** A labelled detail row (sparkle icon + "Label: text"), tinted by status. */
export const InfoRow: React.FC<{ label: string; text: string; status?: InfoRowStatus }> = ({ label, text, status = "fixed" }) => {
    const s = useHealReportViewStyles();
    const variantClass = s[INFO_ROW_VARIANT_CLASS[status]] as string;
    return (
        <div className={`${s.infoRow} ${variantClass}`}>
            <span className={s.infoRowIcon} style={{ color: INFO_ROW_ICON_COLOR[status] }} aria-hidden><SparkleFilled /></span>
            <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.5 }}>
                <span className={s.infoRowLabel}>{label}: </span>{text}
            </div>
        </div>
    );
};

/** Finding-card status icon (Fluent, never a unicode glyph). */
export const StatusIcon: React.FC<{ status: InfoRowStatus; color: string }> = ({ status, color }) => {
    const style = { color, fontSize: 16, flexShrink: 0, display: "inline-flex", alignItems: "center" } as const;
    const icon =
        status === "fixed" ? (
            <CheckmarkCircleFilled />
        ) : status === "appbug" ? (
            <DismissCircleFilled />
        ) : status === "flaky" ? (
            <WarningFilled />
        ) : status === "removed" ? (
            <SubtractCircleFilled />
        ) : (
            <QuestionCircleFilled />
        );
    return (
        <span style={style} role="img" aria-label={STATUS_LABEL[status]}>
            {icon}
        </span>
    );
};

/** Collapsible, color-coded section header with a trailing divider. */
export const Section: React.FC<{ title: string; color: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, color, defaultOpen = true, children }) => {
    const s = useHealReportViewStyles();
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div style={{ marginBottom: 28 }}>
            <button type="button" className={s.sectionHeader} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                <span className={s.sectionChevron} style={{ color }} aria-hidden>
                    {open ? <ChevronDownRegular /> : <ChevronRightRegular />}
                </span>
                <span className={s.sectionTitle} style={{ color }}>{title}</span>
                <span className={s.sectionDivider} />
            </button>
            {open && children}
        </div>
    );
};

/** A single count + label chip in the summary banner. */
export const SummaryStat: React.FC<{ count: number; label: string; color: string }> = ({ count, label, color }) => {
    const s = useHealReportViewStyles();
    return (
        <div className={s.summaryStatItem}>
            <div className={s.summaryStatHeader}>
                <span className={s.summaryStatDot} style={{ backgroundColor: color }} />
                <span className={s.summaryStatLabel}>{label}</span>
            </div>
            <div className={s.summaryStatCount} style={{ color }}>{count}</div>
        </div>
    );
};

/* ── Attribution tag — the categorical "whose change" signal, used identically
 *  in scout + heal. The full phrase rides along as the hover title. ── */

type AttributionMeta = { label: string; color: string };

function attributionMeta(origin?: string | null): AttributionMeta | null {
    switch (origin) {
        // Tests describe what they COVER.
        case "change":
            return { label: "Covers this change", color: "#8b949e" };
        case "adjacent":
            return { label: "Covers existing gap", color: "#8b949e" };
        // App defects / heal findings describe their ORIGIN — who introduced it.
        case "pr_related":
            return { label: "Introduced by this change", color: "#8b949e" };
        case "pre_existing":
            return { label: "Pre-existing", color: "#8b949e" };
        case "unknown":
            return { label: "Origin unclear", color: "#8b949e" };
        default:
            return null;
    }
}

export const AttributionTag: React.FC<{ origin?: string | null; title?: string }> = ({ origin, title }) => {
    const meta = attributionMeta(origin);
    if (!meta) return null;
    return (
        <span
            title={title}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "1px 8px",
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: `${meta.color}26`,
                color: meta.color,
                whiteSpace: "nowrap",
            }}
        >
            {meta.label}
        </span>
    );
};
